import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calendar, User, Loader, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  featured_image_url: string;
  author_name: string;
  published_date: string;
  view_count: number;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalPosts: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextPage: number | null;
  prevPage: number | null;
}

const BlogPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to detect YouTube URLs
  const isYouTubeUrl = (url: string): boolean => {
    if (!url) return false;
    const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
    return youtubeRegex.test(url);
  };

  // Helper function to extract YouTube video ID
  const getYouTubeVideoId = (url: string): string | null => {
    const match = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
    return match ? match[1] : null;
  };

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/blog/public/posts?page=${currentPage}&limit=12`;
      const response = await fetch(apiUrl);
      
      if (!response.ok) throw new Error('Failed to fetch blog posts');
      
      const data = await response.json();
      setPosts(data.posts || []);
      setPagination(data.pagination || null);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Update SEO meta tags when page changes
  useEffect(() => {
    const baseTitle = 'BandCam Blog';
    const pageTitle = currentPage > 1 ? `${baseTitle} - Page ${currentPage}` : baseTitle;
    document.title = pageTitle;

    // Update or create meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'News, tips, and stories from the world of school bands and performance video');

    // Update canonical URL
    const baseUrl = window.location.origin;
    const canonicalUrl = currentPage > 1 
      ? `${baseUrl}/blog?page=${currentPage}`
      : `${baseUrl}/blog`;
    
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // Add/update rel="prev" link
    if (pagination?.hasPrevPage && pagination.prevPage) {
      let prevLink = document.querySelector('link[rel="prev"]');
      if (!prevLink) {
        prevLink = document.createElement('link');
        prevLink.setAttribute('rel', 'prev');
        document.head.appendChild(prevLink);
      }
      const prevUrl = pagination.prevPage === 1 
        ? `${baseUrl}/blog`
        : `${baseUrl}/blog?page=${pagination.prevPage}`;
      prevLink.setAttribute('href', prevUrl);
    } else {
      // Remove prev link if it exists and we're on first page
      const prevLink = document.querySelector('link[rel="prev"]');
      if (prevLink) {
        prevLink.remove();
      }
    }

    // Add/update rel="next" link
    if (pagination?.hasNextPage && pagination.nextPage) {
      let nextLink = document.querySelector('link[rel="next"]');
      if (!nextLink) {
        nextLink = document.createElement('link');
        nextLink.setAttribute('rel', 'next');
        document.head.appendChild(nextLink);
      }
      nextLink.setAttribute('href', `${baseUrl}/blog?page=${pagination.nextPage}`);
    } else {
      // Remove next link if it exists and we're on last page
      const nextLink = document.querySelector('link[rel="next"]');
      if (nextLink) {
        nextLink.remove();
      }
    }
  }, [currentPage, pagination]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-midnight">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-4">
            BandCam Blog
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            News, tips, and stories from the world of school bands and performance video
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader className="w-12 h-12 animate-spin text-sky" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-100 border-2 border-red-500 p-6 rounded-lg text-center">
            <p className="text-red-700 font-semibold">{error}</p>
            <button
              onClick={fetchPosts}
              className="mt-4 bg-red-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-600"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Blog Posts Grid */}
        {!loading && !error && (
          <>
            {posts.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-xl text-gray-400">No blog posts yet. Check back soon!</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {posts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="bg-sky border-2 border-black overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 group"
                    >
                      {/* Featured Image/Video */}
                      {post.featured_image_url && (
                        <div className="aspect-video overflow-hidden border-b-2 border-black">
                          {isYouTubeUrl(post.featured_image_url) ? (
                            <iframe
                              src={`https://www.youtube.com/embed/${getYouTubeVideoId(post.featured_image_url)}`}
                              title={post.title}
                              className="w-full h-full"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          ) : (
                            <img
                              src={post.featured_image_url}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          )}
                        </div>
                      )}

                      {/* Content */}
                      <div className="p-6">
                        {/* Title */}
                        <h2 className="text-2xl font-bold text-black mb-3 group-hover:text-goldie transition-colors">
                          {post.title}
                        </h2>

                        {/* Excerpt */}
                        {post.excerpt && (
                          <p className="text-white-700 mb-4 line-clamp-3">
                            {post.excerpt}
                          </p>
                        )}

                        {/* Meta Information */}
                        <div className="flex items-center justify-between text-sm text-white-600 pt-4 border-t border-gray-300">
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-1" />
                            <span>{post.author_name}</span>
                          </div>
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            <span>{formatDate(post.published_date)}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pagination Controls */}
                {pagination && pagination.totalPages > 1 && (
                  <div className="mt-12 flex flex-col items-center">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-white text-sm">
                        Page {pagination.currentPage} of {pagination.totalPages}
                        {' '}({pagination.totalPosts} total posts)
                      </span>
                    </div>
                    <nav className="flex items-center gap-2" aria-label="Blog pagination">
                      {/* Previous Button */}
                      {pagination.hasPrevPage ? (
                        <Link
                          to={pagination.prevPage === 1 ? '/blog' : `/blog?page=${pagination.prevPage}`}
                          className="bg-sky border-2 border-black px-4 py-2 text-black font-semibold hover:bg-goldie transition-colors flex items-center gap-1"
                          aria-label="Previous page"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Link>
                      ) : (
                        <span className="bg-gray-400 border-2 border-gray-500 px-4 py-2 text-gray-600 font-semibold flex items-center gap-1 cursor-not-allowed">
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </span>
                      )}

                      {/* Page Numbers */}
                      <div className="flex items-center gap-1">
                        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => {
                          // Show first page, last page, current page, and pages around current
                          const showPage = 
                            pageNum === 1 ||
                            pageNum === pagination.totalPages ||
                            (pageNum >= pagination.currentPage - 1 && pageNum <= pagination.currentPage + 1);

                          if (!showPage) {
                            // Show ellipsis
                            if (pageNum === pagination.currentPage - 2 || pageNum === pagination.currentPage + 2) {
                              return (
                                <span key={pageNum} className="px-2 text-white">
                                  ...
                                </span>
                              );
                            }
                            return null;
                          }

                          return (
                            <Link
                              key={pageNum}
                              to={pageNum === 1 ? '/blog' : `/blog?page=${pageNum}`}
                              className={`border-2 px-4 py-2 font-semibold transition-colors ${
                                pageNum === pagination.currentPage
                                  ? 'bg-goldie border-black text-black'
                                  : 'bg-sky border-black text-black hover:bg-goldie'
                              }`}
                              aria-label={`Go to page ${pageNum}`}
                              aria-current={pageNum === pagination.currentPage ? 'page' : undefined}
                            >
                              {pageNum}
                            </Link>
                          );
                        })}
                      </div>

                      {/* Next Button */}
                      {pagination.hasNextPage ? (
                        <Link
                          to={`/blog?page=${pagination.nextPage}`}
                          className="bg-sky border-2 border-black px-4 py-2 text-black font-semibold hover:bg-goldie transition-colors flex items-center gap-1"
                          aria-label="Next page"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      ) : (
                        <span className="bg-gray-400 border-2 border-gray-500 px-4 py-2 text-gray-600 font-semibold flex items-center gap-1 cursor-not-allowed">
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </span>
                      )}
                    </nav>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-sky border-t-2 border-black mt-12 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-black">
            Want to learn more about BandCam?{' '}
            <Link to="/about" className="underline hover:text-gray-700 font-semibold">
              About Us
            </Link>
            {' | '}
            <Link to="/contact" className="underline hover:text-gray-700 font-semibold">
              Contact Us
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;

